/** @jsxImportSource @compiled/react */
import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { css } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import {
	Card,
	TitleBlock,
	ElementName,
	SmartLinkDirection,
	SmartLinkPosition,
	SmartLinkSize,
	type ElementItem,
} from '@atlaskit/smart-card';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import { IconTile, type IconTileProps } from '@atlaskit/icon';
import PinIcon from '@atlaskit/icon/core/pin';
import PinFilledIcon from '@atlaskit/icon/core/pin-filled';
import CrossIcon from '@atlaskit/icon/core/cross';

type LinkData = {
	id: string;
	url: string;
	title: string;
	subtitle?: string;
	isPinned: boolean;
	icon?: IconTileProps['icon'];
	iconAppearance?: IconTileProps['appearance'];
};

type LinkItemProps = {
	link: LinkData;
	onRemove: (linkId: string) => void;
	onPin: (linkId: string) => void;
	isPinned: boolean;
};

const i18n = defineMessages({
	pin: {
		id: 'space-overview.smart-space-overview.link-item.pin',
		defaultMessage: 'Pin',
		description: 'Accessible label for the pin button on a link item.',
	},
	unpin: {
		id: 'space-overview.smart-space-overview.link-item.unpin',
		defaultMessage: 'Unpin',
		description: 'Accessible label for the unpin button on a link item.',
	},
	remove: {
		id: 'space-overview.smart-space-overview.link-item.remove',
		defaultMessage: 'Remove',
		description: 'Accessible label for the remove button on a link item.',
	},
});

/* Compiled css for the SmartCard wrapper — allows child selectors for internal element overrides */
const smartCardWrapperStyle = css({
	flex: 1,
	minWidth: 0,
	/* SmartCard block: remove default padding so our row padding controls spacing */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-block="true"]': {
		padding: 0,
		gap: token('space.100'),
	},
	/* Remove gap between title and subtitle */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-element-group]': {
		gap: 0,
	},
	/* Icon: wrap in a neutral background box to match CardItem style */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-element-icon="true"]': {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.large'),
		width: '40px',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
		flexShrink: 0,
	},
	/* Title link — a[href] specificity beats SmartCard's class-based link color */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& a[href]': {
		color: token('color.text'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		textDecoration: 'none',
	},
});

/* Variant used when an IconTile is provided — hides SmartCard's own icon element */
const smartCardWrapperStyleWithTile = css({
	flex: 1,
	minWidth: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-block="true"]': {
		padding: 0,
		gap: token('space.100'),
	},
	/* Remove gap between title and subtitle */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-element-group]': {
		gap: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& [data-smart-element-icon="true"]': {
		display: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& a[href]': {
		color: token('color.text'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		textDecoration: 'none',
	},
});

const itemStyles = cssMap({
	outerWrapper: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
	},
	row: {
		paddingBlock: token('space.075'),
		paddingInline: token('space.100'),
		borderRadius: token('radius.large'),
		cursor: 'pointer',
		flex: 1,
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:focus-within': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	actions: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		gap: token('space.025'),
		opacity: 0,
		transition: 'opacity 150ms ease',
	},
	actionsVisible: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		gap: token('space.025'),
		opacity: 1,
		transition: 'opacity 150ms ease',
	},
});

const cardUiProps = {
	hideBackground: true,
	hideElevation: true,
	hidePadding: true,
	clickableContainer: true,
	size: SmartLinkSize.Medium,
};

const titleBlockSubtitle: ElementItem[] = [
	{ name: ElementName.Provider },
	{ name: ElementName.CollaboratorGroup },
];

export const LinkItem = ({ link, onRemove, onPin, isPinned = false }: LinkItemProps) => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);

	const handleMouseEnter = useCallback(() => setIsHovered(true), []);
	const handleMouseLeave = useCallback(() => setIsHovered(false), []);
	const handleFocus = useCallback(() => setIsHovered(true), []);
	const handleBlur = useCallback((e: React.FocusEvent<HTMLDivElement>) => {
		if (!e.currentTarget.contains(e.relatedTarget)) {
			setIsHovered(false);
		}
	}, []);

	const handlePin = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			e.stopPropagation();
			e.nativeEvent.stopImmediatePropagation();
			onPin?.(link.id);
		},
		[onPin, link.id],
	);

	const handleRemove = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			e.stopPropagation();
			e.nativeEvent.stopImmediatePropagation();
			onRemove?.(link.id);
		},
		[onRemove, link.id],
	);

	return (
		<Box
			xcss={itemStyles.outerWrapper}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			<Flex
				xcss={itemStyles.row}
				justifyContent="space-between"
				alignItems="center"
				gap="space.100"
			>
				{link.icon && (
					<IconTile
						icon={link.icon}
						label={link.title}
						appearance={link.iconAppearance ?? 'gray'}
						size="large"
					/>
				)}
				{link.icon ? (
					<div css={smartCardWrapperStyleWithTile}>
						<Card appearance="inline" url={link.url} placeholder={link.title} ui={cardUiProps}>
							<TitleBlock
								direction={SmartLinkDirection.Horizontal}
								maxLines={1}
								position={SmartLinkPosition.Center}
								text={link.title}
								anchorTarget="_blank"
								hideTitleTooltip
								subtitle={titleBlockSubtitle}
							/>
						</Card>
					</div>
				) : (
					<div css={smartCardWrapperStyle}>
						<Card appearance="inline" url={link.url} placeholder={link.title} ui={cardUiProps}>
							<TitleBlock
								direction={SmartLinkDirection.Horizontal}
								maxLines={1}
								position={SmartLinkPosition.Center}
								text={link.title}
								anchorTarget="_blank"
								hideTitleTooltip
								subtitle={titleBlockSubtitle}
							/>
						</Card>
					</div>
				)}
				<Box xcss={isPinned || isHovered ? itemStyles.actionsVisible : itemStyles.actions}>
					<IconButton
						icon={isPinned ? PinFilledIcon : PinIcon}
						label={isPinned ? formatMessage(i18n.unpin) : formatMessage(i18n.pin)}
						appearance="subtle"
						spacing="compact"
						onClick={handlePin}
					/>
					{!isPinned && (
						<IconButton
							icon={CrossIcon}
							label={formatMessage(i18n.remove)}
							appearance="subtle"
							spacing="compact"
							onClick={handleRemove}
						/>
					)}
				</Box>
			</Flex>
		</Box>
	);
};

export type { LinkData };
